import { copyLabel, findCopySource } from "../../../../mobile/services/timesheet-copy";
import type { UnitSnapshot } from "../../../../mobile/services/timesheet-days";
import type { WorkloadUnitSnapshotType } from "modules/spending/workload/models/workload-unit";

/**
 * «Как в прошлый день» в окне дня табеля полной версии — те же правила, что у мобильной «Как вчера»
 * (specs/002-pwa-home-timesheet, FR-017 / R10): источник — последний более ранний день с рабочей записью,
 * не дальше 62 дней; копируются рабочие записи без комментариев. Поиск и подпись — общие функции
 * `mobile/services/timesheet-copy`, здесь только даты полной версии (`ГГГГ.ММ.ДД`) и сбор дней.
 */

/** День табеля, как он лежит в сторе полной версии или в ответе `GET /timesheet` */
export interface CopyDay {
    day: string;
    units: readonly WorkloadUnitSnapshotType[];
}

export interface CopySource {
    /** День-источник, `ГГГГ.ММ.ДД` */
    day: string;
    /** Заголовок кнопки полностью: «Как вчера» / «Как в пятницу, 30 октября» */
    when: string;
    /** «Как вчера: Р-217 · 8 ч» / «Как в пн, 12 окт: …» */
    label: string;
    units: readonly WorkloadUnitSnapshotType[];
}

// день недели - в винительном падеже с предлогом, месяц - в родительном: «во вторник, 3 ноября»
const WEEKDAYS_FULL = ["в воскресенье", "в понедельник", "во вторник", "в среду", "в четверг", "в пятницу", "в субботу"];
const MONTHS_FULL = [
    "января",
    "февраля",
    "марта",
    "апреля",
    "мая",
    "июня",
    "июля",
    "августа",
    "сентября",
    "октября",
    "ноября",
    "декабря",
];

/** «Как вчера» / «Как в пятницу, 30 октября» - без сокращений (у мобильной версии подпись короткая) */
export function copyWhen(source: string, target: string): string {
    const [sy, sm, sd] = toIsoDay(source).split("-").map(Number);
    const [ty, tm, td] = toIsoDay(target).split("-").map(Number);
    const sourceMs = Date.UTC(sy, sm - 1, sd);

    if (Date.UTC(ty, tm - 1, td) - sourceMs === 24 * 60 * 60 * 1000) {
        return "Как вчера";
    }

    return `Как ${WEEKDAYS_FULL[new Date(sourceMs).getUTCDay()]}, ${sd} ${MONTHS_FULL[sm - 1]}`;
}

/** `ГГГГ.ММ.ДД` → `ГГГГ-ММ-ДД` */
export function toIsoDay(day: string): string {
    return day.slice(0, 10).replace(/\./g, "-");
}

/**
 * Источник для дня `target` среди `days` (дни текущего и, если загружен, прошлого месяца сотрудника).
 * Ничего нет — `null`
 */
export function findCopyDay(target: string, days: readonly CopyDay[]): CopySource | null {
    const byIso = new Map<string, CopyDay>();
    days.forEach((day) => byIso.set(toIsoDay(day.day), day));

    const lookup = (iso: string) => {
        const found = byIso.get(iso);

        return found ? { units: found.units as unknown as readonly UnitSnapshot[] } : undefined;
    };

    const isoTarget = toIsoDay(target);
    const source = findCopySource(isoTarget, lookup);

    if (!source) {
        return null;
    }

    const found = byIso.get(source) as CopyDay;

    return {
        day: found.day,
        when: copyWhen(found.day, target),
        label: copyLabel(source, isoTarget, found.units as unknown as readonly UnitSnapshot[]),
        units: found.units,
    };
}

/** Год и месяц (1–12) перед месяцем дня `ГГГГ.ММ.ДД` */
export function previousMonth(day: string): { year: number; month: number } {
    const [year, month] = day.split(".").map(Number);

    return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}
